import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp, message } from 'antd';
import { SuperTokensWrapper } from 'supertokens-auth-react';
import { initSuperTokens } from './config/supertokens';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import SplashScreen from './components/common/SplashScreen';
import AppLayout from './components/common/Layout';
import MinimalLayout from './components/common/MinimalLayout';
import ProtectedRoute from './components/common/ProtectedRoute';
import RoleBasedRoute from './components/common/RoleBasedRoute';
import FeatureBasedRoute from './components/common/FeatureBasedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import {
  Homepage,
  TeamPage,
  ProductLandingPage,
  ServicesPage,
  BlogListPage,
  BlogPostPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  TermsPage,
  AcceptTermsPage,
  BrandsDashboard,
  BrandDetailPage,
  BrandSettingsPage,
  MySubredditsPage,
  InboxPage,
  LeadsPage,
  AIReplyGeneratorPage,
  LLMGeoAnalysisPage,
  LLMGeoAnalysisProgressPage,
  LLMGeoAnalysisReportPage,
  CitationAuthorityPage,
  CitationAuthorityReportPage,
  CommunityEngagementPage,
  CommunityEngagementReportPage,
  StructuredDataPage,
  StructuredDataReportPage,
  CrossPlatformPage,
  CrossPlatformReportPage,
  SemanticOptimizationPage,
  SemanticOptimizationReportPage,
  CompetitorAnalysisListPage,
  CompetitorAnalysisPage,
  CompetitorAnalysisDashboardPage,
  AdminDashboard,
  AdminUsersPage,
  AdminSessionsPage,
  FeatureMatrixPage,
  AICreditUsagePage,
  CompetitorAnalysisSettingsPage,
  EngagementTrackingPage,
  WatiTemplatesPage,
  RedditGenericScraperPage,
  WhatsAppConfigPage,
  CreditManagementPage,
  PricingPlansPage,
  ActionPricingPage,
  SupervisorsPage,
  SupervisorDashboardPage,
  CreditDashboardPage,
  NotificationSettingsPage,
  MCPSettingsPage,
  CmsLoginPage,
  CmsDashboard,
  BlogPostsPage,
  BlogPostEditorPage,
  MediaLibraryPage,
  TagManagementPage,
  SiteNavigationPage,
  ContactFormPage,
  FaqsPage,
  FaqEditorPage,
  HeroesPage,
  HeroEditorPage,
  TeamMembersPage,
  TeamMemberEditorPage,
  CaseStudiesPage,
  CaseStudyEditorPage,
  HomepageSectionsPage,
  ServiceSectionsPage,
  ProductSectionsPage,
  CTASectionsPage,
  ContactDetailsPage,
  SEOManagementPage,
  LandingPageContentPage,
  TestFirebase,
  FirebaseSetupTester,
  FirebaseConfigChecker,
} from './lazyRoutes';
import { UserType, Permissions } from './config/supertokens';

// CMS shell (not code-split)
import { CmsAuthProvider } from './cms/contexts/CmsAuthContext';
import CmsProtectedRoute from './cms/components/CmsProtectedRoute';
import CmsLayout from './cms/components/CmsLayout';
import GlobalActionFeedback from './components/common/GlobalActionFeedback';
import GlobalActionRestrictedFeedback from './components/common/GlobalActionRestrictedFeedback';
import InsufficientCreditsModal from './components/common/InsufficientCreditsModal';
import { ReplyComposerProvider } from './contexts/ReplyComposerContext';
import ReplyComposerDrawer from './components/common/reply/ReplyComposerDrawer';
import ReplyMinimizedRail from './components/common/reply/ReplyMinimizedRail';
import InstallPrompt from './components/common/InstallPrompt';



// Initialize SuperTokens before rendering
initSuperTokens();

// Patch AntD message.success to dispatch a global event so we can show a floating confirmation card
try {
  // Preserve original
  const _origSuccess = (message as any).success;
  (message as any).success = (content: any, ...args: any[]) => {
    _origSuccess(content, ...args);
    try {
      let detail: any = {};
      if (typeof content === 'string') {
        detail = { title: content };
      } else if (typeof content === 'object' && content !== null) {
        detail = { title: content.content || content.title || String(content) };
      } else {
        detail = { title: String(content) };
      }
      document.dispatchEvent(new CustomEvent('cms-feedback', { detail }));
    } catch (e) {
      // ignore
    }
  };
} catch (e) {
  // ignore patch failures
}

// Custom theme for Ant Design - Minimal, Apple/Google-inspired design
const theme = {
  token: {
    colorPrimary: '#0284c7',
    colorInfo: '#0284c7',
    colorSuccess: '#059669',
    colorWarning: '#d97706',
    colorError: '#dc2626',
    borderRadius: 8,
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", Inter, "Segoe UI", Roboto, sans-serif',
    colorBgContainer: '#ffffff',
    colorBgLayout: '#f9fafb',
    colorBorder: '#e5e7eb',
    colorBorderSecondary: '#f3f4f6',
    colorText: '#111827',
    colorTextSecondary: '#6b7280',
    colorTextTertiary: '#9ca3af',
    lineHeight: 1.6,
    fontSize: 14,
    controlHeight: 40,
    paddingContentHorizontal: 16,
    paddingContentVertical: 12,
  },
  components: {
    Button: {
      borderRadius: 8,
      controlHeight: 40,
      paddingContentHorizontal: 20,
    },
    Card: {
      borderRadiusLG: 12,
      paddingLG: 24,
    },
    Input: {
      borderRadius: 8,
      controlHeight: 44,
    },
    Select: {
      borderRadius: 8,
      controlHeight: 44,
    },
    Table: {
      borderRadius: 12,
    },
    Modal: {
      borderRadiusLG: 16,
    },
    Tabs: {
      itemColor: '#6b7280',
      itemSelectedColor: '#0284c7',
      itemHoverColor: '#111827',
    },
  },
};

const RouteLoadingFallback: React.FC = () => (
  <div className="min-h-[50vh] flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0284c7] mx-auto mb-3" />
      <p className="text-gray-500 text-sm">Loading…</p>
    </div>
  </div>
);

// Route component that handles authenticated layouts (with sidebar)
const AuthenticatedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute>
      <AppLayout>
        {children}
      </AppLayout>
    </ProtectedRoute>
  );
};

// Route component for brand-centric pages (minimal layout without sidebar)
const BrandRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute>
      <MinimalLayout>
        {children}
      </MinimalLayout>
    </ProtectedRoute>
  );
};

// Route component that handles feature-based access control (with sidebar)
const FeatureRoute: React.FC<{ children: React.ReactNode; requiredFeature: string }> = ({ children, requiredFeature }) => {
  return (
    <FeatureBasedRoute requiredFeature={requiredFeature}>
      <AppLayout>
        {children}
      </AppLayout>
    </FeatureBasedRoute>
  );
};

// Route component for brand-scoped features (minimal layout)
const BrandFeatureRoute: React.FC<{ children: React.ReactNode; requiredFeature: string }> = ({ children, requiredFeature }) => {
  return (
    <FeatureBasedRoute requiredFeature={requiredFeature}>
      <MinimalLayout>
        {children}
      </MinimalLayout>
    </FeatureBasedRoute>
  );
};

// Dashboard router that redirects based on user role
const DashboardRouter: React.FC = () => {
  const { user } = useAuth();
  
  // SUPERADMIN users go to admin dashboard
  if (user?.is_superadmin || user?.user_type === 'superadmin') {
    return <Navigate to="/admin" replace />;
  }

  // SUPERVISOR users only have access to the supervisor dashboard
  if (user?.user_type === 'supervisor') {
    return <Navigate to="/supervisor" replace />;
  }
  
  // All other users go to the new brand-centric dashboard
  return <BrandsDashboard />;
};

// Admin route component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <RoleBasedRoute 
      allowedRoles={[UserType.SUPERADMIN]} 
      requiredPermissions={[Permissions.ADMIN_MANAGE_USERS]}
    >
      <AppLayout>
        {children}
      </AppLayout>
    </RoleBasedRoute>
  );
};

// Supervisor route component (dashboard-only access using a minimal layout)
const SupervisorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <RoleBasedRoute allowedRoles={[UserType.SUPERVISOR, UserType.SUPERADMIN]}>
      <MinimalLayout>
        {children}
      </MinimalLayout>
    </RoleBasedRoute>
  );
};

// Main App component
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Homepage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/products" element={<ProductLandingPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
        } 
      />
      <Route 
        path="/register" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
        } 
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Public legal / terms page — readable without login */}
      <Route path="/terms" element={<TermsPage />} />
      {/* Terms consent gate — requires auth but must NOT itself be behind the terms gate */}
      <Route
        path="/accept-terms"
        element={
          <ProtectedRoute skipTermsCheck>
            <AcceptTermsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <BrandRoute>
            <DashboardRouter />
          </BrandRoute>
        } 
      />
      
      {/* Brand Detail Routes */}
      <Route 
        path="/brands/:brandId" 
        element={
          <BrandRoute>
            <BrandDetailPage />
          </BrandRoute>
        } 
      />
      
      {/* Brand Settings Page */}
      <Route 
        path="/brands/:brandId/settings" 
        element={
          <BrandRoute>
            <BrandSettingsPage />
          </BrandRoute>
        } 
      />
      
      {/* Brand-scoped Audit Routes */}
      <Route 
        path="/brands/:brandId/audits/full-analysis" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_full_analysis">
            <LLMGeoAnalysisPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/full-analysis/report/:reportId" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_full_analysis">
            <LLMGeoAnalysisReportPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/citation-authority" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_citation_authority">
            <CitationAuthorityPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/citation-authority/report/:reportId" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_citation_authority">
            <CitationAuthorityReportPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/community-engagement" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_community_engagement">
            <CommunityEngagementPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/community-engagement/report/:reportId" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_community_engagement">
            <CommunityEngagementReportPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/structured-data" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_structured_data">
            <StructuredDataPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/structured-data/report/:reportId" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_structured_data">
            <StructuredDataReportPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/cross-platform" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_cross_platform">
            <CrossPlatformPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/cross-platform/report/:reportId" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_cross_platform">
            <CrossPlatformReportPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/semantic-optimization" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_semantic_optimization">
            <SemanticOptimizationPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/audits/semantic-optimization/report/:reportId" 
        element={
          <BrandFeatureRoute requiredFeature="llm_geo_semantic_optimization">
            <SemanticOptimizationReportPage />
          </BrandFeatureRoute>
        } 
      />
      
      {/* Brand-scoped Competitor Analysis Routes */}
      <Route 
        path="/brands/:brandId/competitor-analysis" 
        element={
          <BrandFeatureRoute requiredFeature="competitor_analysis">
            <CompetitorAnalysisDashboardPage />
          </BrandFeatureRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/competitor-analysis/setup" 
        element={
          <BrandFeatureRoute requiredFeature="competitor_analysis">
            <CompetitorAnalysisPage />
          </BrandFeatureRoute>
        } 
      />
      
      {/* Brand-scoped Community Routes */}
      <Route 
        path="/brands/:brandId/subreddits" 
        element={
          <BrandRoute>
            <MySubredditsPage />
          </BrandRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/inbox/unread" 
        element={
          <BrandRoute>
            <InboxPage view="unread" />
          </BrandRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/inbox/all" 
        element={
          <BrandRoute>
            <InboxPage view="all" />
          </BrandRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/inbox/saved" 
        element={
          <BrandRoute>
            <InboxPage view="saved" />
          </BrandRoute>
        } 
      />
      <Route 
        path="/brands/:brandId/inbox/replied" 
        element={
          <BrandRoute>
            <InboxPage view="replied" />
          </BrandRoute>
        } 
      />
      
      {/* Brand-scoped Notification Settings */}
      <Route 
        path="/brands/:brandId/notifications" 
        element={
          <BrandRoute>
            <NotificationSettingsPage />
          </BrandRoute>
        } 
      />
      
      {/* MCP Integration Settings */}
      <Route 
        path="/settings/mcp" 
        element={
          <BrandRoute>
            <MCPSettingsPage />
          </BrandRoute>
        } 
      />
      
      {/* Credits Dashboard */}
      <Route
        path="/credits"
        element={
          <BrandRoute>
            <CreditDashboardPage />
          </BrandRoute>
        }
      />

      {/* Global Tools Route */}
      <Route 
        path="/tools/ai-reply-generator" 
        element={
          <BrandRoute>
            <AIReplyGeneratorPage />
          </BrandRoute>
        } 
      />
      
      {/* Legacy Routes (keep for backwards compatibility) */}
      <Route 
        path="/my-subreddits" 
        element={
          <AuthenticatedRoute>
            <MySubredditsPage />
          </AuthenticatedRoute>
        } 
      />
      
      
      <Route 
        path="/inbox/unread" 
        element={
          <AuthenticatedRoute>
            <InboxPage view="unread" />
          </AuthenticatedRoute>
        } 
      />
      
      <Route 
        path="/inbox/saved" 
        element={
          <AuthenticatedRoute>
            <InboxPage view="saved" />
          </AuthenticatedRoute>
        } 
      />
      
      <Route 
        path="/inbox/replied" 
        element={
          <AuthenticatedRoute>
            <InboxPage view="replied" />
          </AuthenticatedRoute>
        } 
      />
      
      <Route 
        path="/inbox/all" 
        element={
          <AuthenticatedRoute>
            <InboxPage view="all" />
          </AuthenticatedRoute>
        } 
      />
      
      <Route 
        path="/ai-reply-generator" 
        element={
          <AuthenticatedRoute>
            <AIReplyGeneratorPage />
          </AuthenticatedRoute>
        } 
      />
      
      {/* LLM Geo Analysis - Full Analysis Routes */}
      <Route 
        path="/llm-geo-analysis" 
        element={
          <FeatureRoute requiredFeature="llm_geo_full_analysis">
            <LLMGeoAnalysisPage />
          </FeatureRoute>
        } 
      />
      
      <Route 
        path="/llm-geo-analysis/progress/:reportId" 
        element={
          <FeatureRoute requiredFeature="llm_geo_full_analysis">
            <LLMGeoAnalysisProgressPage />
          </FeatureRoute>
        } 
      />
      
      <Route 
        path="/llm-geo-analysis/report/:reportId" 
        element={
          <FeatureRoute requiredFeature="llm_geo_full_analysis">
            <LLMGeoAnalysisReportPage />
          </FeatureRoute>
        } 
      />
      
      {/* Citation Authority Routes */}
      <Route 
        path="/citation-authority" 
        element={
          <FeatureRoute requiredFeature="llm_geo_citation_authority">
            <CitationAuthorityPage />
          </FeatureRoute>
        } 
      />
      
      <Route 
        path="/citation-authority/report/:reportId" 
        element={
          <FeatureRoute requiredFeature="llm_geo_citation_authority">
            <CitationAuthorityReportPage />
          </FeatureRoute>
        } 
      />
      
      {/* Community Engagement Routes */}
      <Route 
        path="/community-engagement" 
        element={
          <FeatureRoute requiredFeature="llm_geo_community_engagement">
            <CommunityEngagementPage />
          </FeatureRoute>
        } 
      />
      
      <Route 
        path="/community-engagement/report/:reportId" 
        element={
          <FeatureRoute requiredFeature="llm_geo_community_engagement">
            <CommunityEngagementReportPage />
          </FeatureRoute>
        } 
      />
      
      {/* Structured Data Routes */}
      <Route 
        path="/structured-data" 
        element={
          <FeatureRoute requiredFeature="llm_geo_structured_data">
            <StructuredDataPage />
          </FeatureRoute>
        } 
      />
      
      <Route 
        path="/structured-data/report/:reportId" 
        element={
          <FeatureRoute requiredFeature="llm_geo_structured_data">
            <StructuredDataReportPage />
          </FeatureRoute>
        } 
      />
      
      {/* Cross-Platform Routes */}
      <Route 
        path="/cross-platform" 
        element={
          <FeatureRoute requiredFeature="llm_geo_cross_platform">
            <CrossPlatformPage />
          </FeatureRoute>
        } 
      />
      
      <Route 
        path="/cross-platform/report/:reportId" 
        element={
          <FeatureRoute requiredFeature="llm_geo_cross_platform">
            <CrossPlatformReportPage />
          </FeatureRoute>
        } 
      />
      
      {/* Semantic Optimization Routes */}
      <Route 
        path="/semantic-optimization" 
        element={
          <FeatureRoute requiredFeature="llm_geo_semantic_optimization">
            <SemanticOptimizationPage />
          </FeatureRoute>
        } 
      />
      
      <Route 
        path="/semantic-optimization/report/:reportId" 
        element={
          <FeatureRoute requiredFeature="llm_geo_semantic_optimization">
            <SemanticOptimizationReportPage />
          </FeatureRoute>
        } 
      />
      
      {/* Competitor Analysis Routes */}
      <Route 
        path="/competitor-analysis" 
        element={
          <FeatureRoute requiredFeature="competitor_analysis">
            <CompetitorAnalysisListPage />
          </FeatureRoute>
        } 
      />
      
      <Route 
        path="/competitor-analysis/new" 
        element={
          <FeatureRoute requiredFeature="competitor_analysis">
            <CompetitorAnalysisPage />
          </FeatureRoute>
        } 
      />
      
      <Route 
        path="/competitor-analysis/dashboard/:configId" 
        element={
          <FeatureRoute requiredFeature="competitor_analysis">
            <CompetitorAnalysisDashboardPage />
          </FeatureRoute>
        } 
      />
      
      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } 
      />
      
      <Route 
        path="/admin/users" 
        element={
          <AdminRoute>
            <AdminUsersPage />
          </AdminRoute>
        } 
      />
      
      <Route 
        path="/admin/sessions" 
        element={
          <AdminRoute>
            <AdminSessionsPage />
          </AdminRoute>
        } 
      />
      
      <Route 
        path="/admin/feature-matrix" 
        element={
          <AdminRoute>
            <FeatureMatrixPage />
          </AdminRoute>
        } 
      />
      
      <Route 
        path="/admin/ai-credit-usage" 
        element={
          <AdminRoute>
            <AICreditUsagePage />
          </AdminRoute>
        } 
      />
      
      <Route 
        path="/admin/competitor-analysis-settings" 
        element={
          <AdminRoute>
            <CompetitorAnalysisSettingsPage />
          </AdminRoute>
        } 
      />

      <Route
        path="/admin/engagement-tracking"
        element={
          <AdminRoute>
            <EngagementTrackingPage />
          </AdminRoute>
        }
      />
      
      <Route 
        path="/admin/whatsapp-templates" 
        element={
          <AdminRoute>
            <WatiTemplatesPage />
          </AdminRoute>
        } 
      />

      <Route
        path="/admin/reddit-generic-scraper"
        element={
          <AdminRoute>
            <RedditGenericScraperPage />
          </AdminRoute>
        }
      />

      <Route
        path="/admin/whatsapp-config"
        element={
          <AdminRoute>
            <WhatsAppConfigPage />
          </AdminRoute>
        }
      />

      {/* Admin Credit Routes */}
      <Route
        path="/admin/credit-management"
        element={
          <AdminRoute>
            <CreditManagementPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/pricing-plans"
        element={
          <AdminRoute>
            <PricingPlansPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/action-pricing"
        element={
          <AdminRoute>
            <ActionPricingPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/supervisors"
        element={
          <AdminRoute>
            <SupervisorsPage />
          </AdminRoute>
        }
      />

      {/* Supervisor Dashboard (read-only access for supervisor user_type) */}
      <Route
        path="/supervisor"
        element={
          <SupervisorRoute>
            <SupervisorDashboardPage />
          </SupervisorRoute>
        }
      />
      
      {/* Mobile-first Leads Swipe Page */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <LeadsPage />
          </ProtectedRoute>
        }
      />

      {/* Redirect /inbox to /inbox/unread */}
      <Route 
        path="/inbox" 
        element={<Navigate to="/inbox/unread" replace />}
      />
      
      {/* CMS Routes - Separate Firebase-powered module */}
      <Route 
        path="/cms/setup" 
        element={<FirebaseSetupTester />}
      />
      

      <Route 
        path="/firebase-config" 
        element={<FirebaseConfigChecker />}
      />
      

      <Route 
        path="/cms/login" 
        element={
          <CmsAuthProvider>
            <CmsLoginPage />
          </CmsAuthProvider>
        }
      />
      
      <Route path="/cms/*" element={
        <CmsAuthProvider>
          <CmsProtectedRoute>
            <CmsLayout>
              <Routes>
                <Route path="dashboard" element={<CmsProtectedRoute><CmsDashboard /></CmsProtectedRoute>} />
                <Route path="posts" element={<CmsProtectedRoute><BlogPostsPage /></CmsProtectedRoute>} />
                <Route path="posts/new" element={<CmsProtectedRoute><BlogPostEditorPage /></CmsProtectedRoute>} />
                <Route path="posts/:id/edit" element={<CmsProtectedRoute><BlogPostEditorPage /></CmsProtectedRoute>} />
                <Route path="team-members" element={<CmsProtectedRoute><TeamMembersPage /></CmsProtectedRoute>} />
                <Route path="team-members/new" element={<CmsProtectedRoute><TeamMemberEditorPage /></CmsProtectedRoute>} />
                <Route path="team-members/:id/edit" element={<CmsProtectedRoute><TeamMemberEditorPage /></CmsProtectedRoute>} />
                <Route path="case-studies" element={<CmsProtectedRoute><CaseStudiesPage /></CmsProtectedRoute>} />
                <Route path="case-studies/new" element={<CmsProtectedRoute><CaseStudyEditorPage /></CmsProtectedRoute>} />
                <Route path="case-studies/:id/edit" element={<CmsProtectedRoute><CaseStudyEditorPage /></CmsProtectedRoute>} />
                <Route path="homepage-sections" element={<CmsProtectedRoute><HomepageSectionsPage /></CmsProtectedRoute>} />
                <Route path="service-sections" element={<CmsProtectedRoute><ServiceSectionsPage /></CmsProtectedRoute>} />
                <Route path="product-sections" element={<CmsProtectedRoute><ProductSectionsPage /></CmsProtectedRoute>} />
                <Route path="cta-sections" element={<CmsProtectedRoute><CTASectionsPage /></CmsProtectedRoute>} />
                <Route path="contact-details" element={<CmsProtectedRoute><ContactDetailsPage /></CmsProtectedRoute>} />
                <Route path="landing-content" element={<CmsProtectedRoute><LandingPageContentPage /></CmsProtectedRoute>} />
                <Route path="test-firebase" element={<CmsProtectedRoute><TestFirebase /></CmsProtectedRoute>} />
                <Route path="faqs" element={<CmsProtectedRoute><FaqsPage /></CmsProtectedRoute>} />
                <Route path="faqs/new" element={<CmsProtectedRoute><FaqEditorPage /></CmsProtectedRoute>} />
                <Route path="faqs/:id/edit" element={<CmsProtectedRoute><FaqEditorPage /></CmsProtectedRoute>} />
                <Route path="heroes" element={<CmsProtectedRoute><HeroesPage /></CmsProtectedRoute>} />
                <Route path="heroes/:page/edit" element={<CmsProtectedRoute><HeroEditorPage /></CmsProtectedRoute>} />
                <Route path="seo" element={<CmsProtectedRoute><SEOManagementPage /></CmsProtectedRoute>} />
                <Route path="media" element={<CmsProtectedRoute><MediaLibraryPage /></CmsProtectedRoute>} />
                <Route path="navigation" element={<CmsProtectedRoute><SiteNavigationPage /></CmsProtectedRoute>} />
                <Route path="contact-form" element={<CmsProtectedRoute><ContactFormPage /></CmsProtectedRoute>} />
                <Route path="tags" element={<CmsProtectedRoute><TagManagementPage /></CmsProtectedRoute>} />
                <Route path="" element={<Navigate to="/cms/dashboard" replace />} />
              </Routes>
            </CmsLayout>
          </CmsProtectedRoute>
        </CmsAuthProvider>
      } />
      
      {/* Catch all route - 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
};

const App: React.FC = () => {
  // SuperTokens already initialized earlier

  return (
    <SuperTokensWrapper>
      <ConfigProvider theme={theme}>
        <AntApp>
          <AuthProvider>
            <LoadingProvider>
              <ReplyComposerProvider>
                <AppWithLoading />
              </ReplyComposerProvider>
            </LoadingProvider>
          </AuthProvider>
        </AntApp>
      </ConfigProvider>
    </SuperTokensWrapper>
  );
};

// Wrapper component that has access to LoadingContext
const AppWithLoading: React.FC = () => {
  const { isGlobalLoading, markAsReady } = useLoading();
  const [creditModalOpen, setCreditModalOpen] = React.useState(false);
  const [creditModalData, setCreditModalData] = React.useState({ required: 0, balance: 0, action: '' });

  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      setCreditModalData({
        required: detail.required || 0,
        balance: detail.balance || 0,
        action: detail.action || '',
      });
      setCreditModalOpen(true);
    };
    document.addEventListener('insufficient-credits', handler);
    return () => document.removeEventListener('insufficient-credits', handler);
  }, []);
  
  // Skip loading screen for CMS, dashboard, login and app routes
  React.useEffect(() => {
    const path = window.location.pathname;
    const skipLoadingRoutes = 
      path.startsWith('/cms') || 
      path.startsWith('/brands') || 
      path.startsWith('/login') || 
      path.startsWith('/signup') ||
      path.startsWith('/reset-password') ||
      path === '/dashboard' ||
      path.startsWith('/inbox') ||
      path.startsWith('/my-subreddits') ||
      path.startsWith('/tools') ||
      path.startsWith('/app') ||
      path.startsWith('/admin');
    
    if (skipLoadingRoutes) {
      markAsReady();
    }
  }, [markAsReady]);
  
  return (
    <>
      <SplashScreen show={isGlobalLoading} />
      <Router>
        <div className="App h-full">
          <GlobalActionFeedback />
          <GlobalActionRestrictedFeedback />
          <InsufficientCreditsModal
            open={creditModalOpen}
            onClose={() => setCreditModalOpen(false)}
            required={creditModalData.required}
            balance={creditModalData.balance}
            action={creditModalData.action}
          />
          {/* Always-mounted reply composer — persists across navigation */}
          <ReplyComposerDrawer />
          <ReplyMinimizedRail />
          <InstallPrompt />
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </div>
      </Router>
    </>
  );
};

export default App;
